import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts'
import {
  TrendingUp,
  FileText,
  Percent,
  Clock,
  CheckCircle,
  AlertTriangle,
  X,
  FolderOpen,
  Eye,
  RefreshCw,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  Shield,
  Zap,
  Target,
  Layers,
  Award,
  Info,
  Download,
  BarChart3,
  PieChart as PieChartIcon,
  ScanLine,
  ImageIcon,
  FileSearch
} from 'lucide-react'
import { specificationsApi } from '../../services/api'

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899']

// Friendly names for extraction methods
const EXTRACTION_METHOD_NAMES = {
  'DIRECT_TEXT': { name: 'Clean PDFs', description: 'Text extracted directly from digital PDFs', icon: FileText },
  'OCR': { name: 'Scanned Images', description: 'Text recognized from scanned documents', icon: ImageIcon },
  'AI_ENHANCED': { name: 'AI Assisted', description: 'Enhanced extraction using AI', icon: Zap },
  'HYBRID': { name: 'Combined Methods', description: 'Multiple methods used together', icon: Layers }
}

// Friendly names for profile types
const PROFILE_TYPE_NAMES = {
  'FRAME': 'Frame Profiles',
  'TUBE': 'Tube Profiles',
  'ANGLE': 'Angle Profiles',
  'CHANNEL': 'Channel Profiles',
  'T_SECTION': 'T-Section Profiles',
  'FLAT': 'Flat Profiles',
  'CUSTOM': 'Custom Profiles'
}

export default function StatisticsDashboard({ stats, procStats }) {
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [showHelp, setShowHelp] = useState(false)
  const [expandedSection, setExpandedSection] = useState(null)

  // Prepare profile type data for pie chart
  const profileTypeData = stats?.by_profile_type
    ? Object.entries(stats.by_profile_type).map(([name, value]) => ({
        name: PROFILE_TYPE_NAMES[name] || name.replace('_', ' ').toUpperCase(),
        originalName: name,
        value
      }))
    : []

  // Find most common profile type
  const mostCommonProfile = profileTypeData.length > 0
    ? profileTypeData.reduce((max, item) => item.value > max.value ? item : max, profileTypeData[0])
    : null

  // Prepare alloy data for bar chart
  const alloyData = stats?.by_alloy
    ? Object.entries(stats.by_alloy)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([name, value]) => ({ name, count: value }))
    : []

  // Find most common alloy
  const mostCommonAlloy = alloyData.length > 0 ? alloyData[0] : null

  // Prepare extraction method data with friendly names
  const extractionData = stats?.by_extraction_method
    ? Object.entries(stats.by_extraction_method).map(([name, value]) => ({
        originalName: name,
        name: EXTRACTION_METHOD_NAMES[name]?.name || name,
        description: EXTRACTION_METHOD_NAMES[name]?.description || 'Extraction method',
        icon: EXTRACTION_METHOD_NAMES[name]?.icon || FileText,
        value
      }))
    : []

  // Calculate health metrics
  const totalFiles = stats?.total_count || 0
  const highConfidenceCount = stats?.high_confidence_count || 0
  const lowConfidenceCount = stats?.low_confidence_count || 0
  const mediumConfidenceCount = totalFiles - highConfidenceCount - lowConfidenceCount
  const healthScore = totalFiles > 0 ? Math.round((highConfidenceCount / totalFiles) * 100) : 0

  // Generate action recommendations
  const getRecommendations = () => {
    const recommendations = []

    if (lowConfidenceCount > 10) {
      recommendations.push({
        type: 'warning',
        title: 'Review Low Confidence Files',
        message: `${lowConfidenceCount} files have uncertain data. Reviewing them will improve search accuracy.`,
        action: () => setShowReviewModal(true),
        actionLabel: 'View Files'
      })
    }

    const ocrCount = extractionData.find(e => e.originalName === 'OCR')?.value || 0
    if (ocrCount > totalFiles * 0.5) {
      recommendations.push({
        type: 'info',
        title: 'Many Scanned Documents',
        message: 'Most of your files are scanned images. Digital PDFs provide better accuracy.',
        actionLabel: 'Learn More'
      })
    }

    if (procStats?.files_processed_today === 0 && procStats?.files_processed_this_week === 0) {
      recommendations.push({
        type: 'info',
        title: 'No Recent Activity',
        message: 'Your library hasn\'t been updated recently. Check for new files to process.',
        actionLabel: 'Check Now'
      })
    }

    if (healthScore >= 90) {
      recommendations.push({
        type: 'success',
        title: 'Excellent Library Health',
        message: 'Your library is in great shape! Search results will be highly accurate.',
        actionLabel: null
      })
    }

    return recommendations
  }

  const recommendations = getRecommendations()

  return (
    <div className="space-y-6">
      {/* Library Summary - Plain Language Overview */}
      <div className="card bg-gradient-to-br from-primary-50 via-white to-accent-purple/5 border-primary-100">
        <div className="card-body">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center">
              <div className="p-3 rounded-xl bg-gradient-to-br from-primary-500 to-accent-purple shadow-lg shadow-primary-500/30">
                <Award className="w-6 h-6 text-white" />
              </div>
              <div className="ml-4">
                <h2 className="text-xl font-bold text-gray-800">Your Library at a Glance</h2>
                <p className="text-sm text-gray-500">A simple summary of your die drawings collection</p>
              </div>
            </div>
            <button
              onClick={() => setShowHelp(!showHelp)}
              className="flex items-center text-sm text-gray-500 hover:text-primary-600 transition-colors"
            >
              <HelpCircle className="w-4 h-4 mr-1" />
              {showHelp ? 'Hide Guide' : 'What is this?'}
            </button>
          </div>

          {/* Help Section */}
          {showHelp && (
            <div className="mb-6 p-4 rounded-xl bg-white border border-gray-100 animate-fade-in">
              <p className="text-sm text-gray-600 mb-3">
                <strong>This page shows detailed information about your drawing library.</strong> Here's what each section means:
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 mr-2 mt-0.5 text-success-500 flex-shrink-0" />
                  <span><strong>Library Health</strong> - How reliable your search results will be</span>
                </li>
                <li className="flex items-start">
                  <PieChartIcon className="w-4 h-4 mr-2 mt-0.5 text-primary-500 flex-shrink-0" />
                  <span><strong>Profile Types</strong> - The different shapes of die drawings you have</span>
                </li>
                <li className="flex items-start">
                  <BarChart3 className="w-4 h-4 mr-2 mt-0.5 text-accent-purple flex-shrink-0" />
                  <span><strong>Alloys</strong> - The aluminum material codes in your drawings</span>
                </li>
                <li className="flex items-start">
                  <ScanLine className="w-4 h-4 mr-2 mt-0.5 text-warning-500 flex-shrink-0" />
                  <span><strong>How Files Were Read</strong> - Methods used to extract data from your PDFs</span>
                </li>
              </ul>
            </div>
          )}

          {/* Summary Stats in Plain Language */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 rounded-xl bg-white border border-gray-100 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <FileText className="w-5 h-5 text-primary-500" />
                <span className="text-xs text-gray-400">Total</span>
              </div>
              <p className="text-3xl font-bold text-gray-800">{totalFiles.toLocaleString()}</p>
              <p className="text-sm text-gray-600 mt-1">Drawings analyzed</p>
              <p className="text-xs text-gray-400 mt-0.5">Ready for searching</p>
            </div>

            <div className="p-4 rounded-xl bg-white border border-gray-100 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <Shield className="w-5 h-5 text-success-500" />
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  healthScore >= 80 ? 'bg-success-100 text-success-700' :
                  healthScore >= 60 ? 'bg-warning-100 text-warning-700' :
                  'bg-danger-100 text-danger-700'
                }`}>
                  {healthScore >= 80 ? 'Excellent' : healthScore >= 60 ? 'Good' : 'Needs Work'}
                </span>
              </div>
              <p className="text-3xl font-bold text-gray-800">{healthScore}%</p>
              <p className="text-sm text-gray-600 mt-1">Data reliability</p>
              <p className="text-xs text-gray-400 mt-0.5">Higher = better search results</p>
            </div>

            <div className="p-4 rounded-xl bg-white border border-gray-100 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <Layers className="w-5 h-5 text-accent-purple" />
                <span className="text-xs text-gray-400">Most Common</span>
              </div>
              <p className="text-xl font-bold text-gray-800 truncate">
                {mostCommonProfile?.name || 'N/A'}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {mostCommonProfile ? `${mostCommonProfile.value} drawings` : 'No data'}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                {mostCommonProfile ? `${((mostCommonProfile.value / totalFiles) * 100).toFixed(0)}% of your library` : ''}
              </p>
            </div>

            <div className="p-4 rounded-xl bg-white border border-gray-100 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <Target className="w-5 h-5 text-warning-500" />
                <span className="text-xs text-gray-400">Top Alloy</span>
              </div>
              <p className="text-xl font-bold text-gray-800 truncate">
                {mostCommonAlloy?.name || 'N/A'}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {mostCommonAlloy ? `${mostCommonAlloy.count} drawings` : 'No data'}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                {mostCommonAlloy ? `${((mostCommonAlloy.count / totalFiles) * 100).toFixed(0)}% of your library` : ''}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Recommendations */}
      {recommendations.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-600 flex items-center">
            <Lightbulb className="w-4 h-4 mr-2 text-warning-500" />
            Suggestions for You
          </h3>
          {recommendations.map((rec, index) => (
            <div
              key={index}
              className={`p-4 rounded-xl border flex items-start justify-between ${
                rec.type === 'warning' ? 'bg-warning-50 border-warning-200' :
                rec.type === 'success' ? 'bg-success-50 border-success-200' :
                'bg-primary-50 border-primary-200'
              }`}
            >
              <div className="flex items-start">
                {rec.type === 'warning' ? (
                  <AlertTriangle className="w-5 h-5 text-warning-600 mr-3 mt-0.5 flex-shrink-0" />
                ) : rec.type === 'success' ? (
                  <CheckCircle className="w-5 h-5 text-success-600 mr-3 mt-0.5 flex-shrink-0" />
                ) : (
                  <Info className="w-5 h-5 text-primary-600 mr-3 mt-0.5 flex-shrink-0" />
                )}
                <div>
                  <p className={`font-medium ${
                    rec.type === 'warning' ? 'text-warning-800' :
                    rec.type === 'success' ? 'text-success-800' :
                    'text-primary-800'
                  }`}>{rec.title}</p>
                  <p className={`text-sm mt-0.5 ${
                    rec.type === 'warning' ? 'text-warning-700' :
                    rec.type === 'success' ? 'text-success-700' :
                    'text-primary-700'
                  }`}>{rec.message}</p>
                </div>
              </div>
              {rec.actionLabel && rec.action && (
                <button
                  onClick={rec.action}
                  className={`text-sm font-medium px-3 py-1.5 rounded-lg transition-colors flex-shrink-0 ${
                    rec.type === 'warning' ? 'bg-warning-200 text-warning-800 hover:bg-warning-300' :
                    'bg-primary-200 text-primary-800 hover:bg-primary-300'
                  }`}
                >
                  {rec.actionLabel}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Library Health - Simplified Traffic Light System */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-gradient-to-br from-success-100 to-success-50 mr-3">
              <Shield className="w-5 h-5 text-success-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Library Health</h3>
              <p className="text-xs text-gray-500">How reliable is your data?</p>
            </div>
          </div>
          <button
            onClick={() => setExpandedSection(expandedSection === 'health' ? null : 'health')}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center"
          >
            {expandedSection === 'health' ? 'Less' : 'More'} details
            {expandedSection === 'health' ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
          </button>
        </div>
        <div className="card-body">
          {/* Traffic Light Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div
              className="p-4 rounded-xl bg-success-50 border border-success-200 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => {}}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <div className="w-3 h-3 rounded-full bg-success-500 mr-2 animate-pulse" />
                  <span className="font-semibold text-success-800">Highly Reliable</span>
                </div>
                <CheckCircle className="w-5 h-5 text-success-500" />
              </div>
              <p className="text-3xl font-bold text-success-700">{highConfidenceCount.toLocaleString()}</p>
              <p className="text-sm text-success-600 mt-1">Search results from these are accurate</p>
            </div>

            <div
              className="p-4 rounded-xl bg-warning-50 border border-warning-200 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => {}}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <div className="w-3 h-3 rounded-full bg-warning-500 mr-2" />
                  <span className="font-semibold text-warning-800">Good Enough</span>
                </div>
                <AlertTriangle className="w-5 h-5 text-warning-500" />
              </div>
              <p className="text-3xl font-bold text-warning-700">{mediumConfidenceCount.toLocaleString()}</p>
              <p className="text-sm text-warning-600 mt-1">Results are usable but may vary slightly</p>
            </div>

            <div
              className={`p-4 rounded-xl bg-danger-50 border border-danger-200 cursor-pointer hover:shadow-md transition-shadow ${lowConfidenceCount > 0 ? '' : 'opacity-50'}`}
              onClick={() => lowConfidenceCount > 0 && setShowReviewModal(true)}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <div className="w-3 h-3 rounded-full bg-danger-500 mr-2" />
                  <span className="font-semibold text-danger-800">Needs Checking</span>
                </div>
                {lowConfidenceCount > 0 && <Eye className="w-5 h-5 text-danger-500" />}
              </div>
              <p className="text-3xl font-bold text-danger-700">{lowConfidenceCount.toLocaleString()}</p>
              <p className="text-sm text-danger-600 mt-1">
                {lowConfidenceCount > 0 ? 'Click to review these files' : 'No files need attention'}
              </p>
            </div>
          </div>

          {/* Expanded Details */}
          {expandedSection === 'health' && (
            <div className="pt-4 border-t border-gray-100 animate-fade-in">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">What does this mean?</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="p-3 rounded-lg bg-gray-50">
                  <p className="font-medium text-gray-700 mb-1">🟢 Highly Reliable (80%+)</p>
                  <p className="text-gray-600">The system is confident about the extracted data. Search results will be accurate.</p>
                </div>
                <div className="p-3 rounded-lg bg-gray-50">
                  <p className="font-medium text-gray-700 mb-1">🟡 Good Enough (50-80%)</p>
                  <p className="text-gray-600">Data is usable but some details may be slightly off. Still good for searching.</p>
                </div>
                <div className="p-3 rounded-lg bg-gray-50">
                  <p className="font-medium text-gray-700 mb-1">🔴 Needs Checking (&lt;50%)</p>
                  <p className="text-gray-600">The system struggled with these files. Manual review recommended for accuracy.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* What's in Your Library - Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile Type Distribution */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-gradient-to-br from-primary-100 to-primary-50 mr-3">
                <PieChartIcon className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Profile Types</h3>
                <p className="text-xs text-gray-500">The variety of die shapes in your library</p>
              </div>
            </div>
          </div>
          <div className="card-body">
            {profileTypeData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={profileTypeData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name.split(' ')[0]} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={90}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {profileTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => [`${value} drawings`, name]}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
                {mostCommonProfile && (
                  <div className="mt-4 p-3 rounded-lg bg-primary-50 text-sm">
                    <p className="text-primary-800">
                      <strong>{mostCommonProfile.name}</strong> is your most common profile type with {mostCommonProfile.value} drawings ({((mostCommonProfile.value / totalFiles) * 100).toFixed(0)}% of your library).
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-[300px] text-gray-500">
                <PieChartIcon className="w-12 h-12 text-gray-300 mb-3" />
                <p className="font-medium">No profile data available</p>
                <p className="text-sm">Process some files to see distribution</p>
              </div>
            )}
          </div>
        </div>

        {/* Alloy Distribution */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-gradient-to-br from-accent-purple/20 to-accent-purple/10 mr-3">
                <BarChart3 className="w-5 h-5 text-accent-purple" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Most Used Alloys</h3>
                <p className="text-xs text-gray-500">Top aluminum material codes in your drawings</p>
              </div>
            </div>
          </div>
          <div className="card-body">
            {alloyData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={alloyData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={70} tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(value) => [`${value} drawings`, 'Count']}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                    />
                    <Bar dataKey="count" fill="#8B5CF6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                {mostCommonAlloy && (
                  <div className="mt-4 p-3 rounded-lg bg-purple-50 text-sm">
                    <p className="text-purple-800">
                      <strong>{mostCommonAlloy.name}</strong> is your most common alloy with {mostCommonAlloy.count} drawings.
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-[300px] text-gray-500">
                <BarChart3 className="w-12 h-12 text-gray-300 mb-3" />
                <p className="font-medium">No alloy data available</p>
                <p className="text-sm">Process some files to see distribution</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* How Files Were Read */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-gradient-to-br from-warning-100 to-warning-50 mr-3">
              <ScanLine className="w-5 h-5 text-warning-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">How Your Files Were Read</h3>
              <p className="text-xs text-gray-500">Methods used to extract data from your PDFs</p>
            </div>
          </div>
        </div>
        <div className="card-body">
          {extractionData.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {extractionData.map((method, index) => {
                const IconComponent = method.icon
                return (
                  <div key={method.originalName} className="text-center p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div
                      className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-3"
                      style={{ backgroundColor: `${COLORS[index % COLORS.length]}15` }}
                    >
                      <IconComponent
                        className="w-8 h-8"
                        style={{ color: COLORS[index % COLORS.length] }}
                      />
                    </div>
                    <p className="text-2xl font-bold text-gray-800">{method.value.toLocaleString()}</p>
                    <p className="font-medium text-gray-700 mt-1">{method.name}</p>
                    <p className="text-xs text-gray-500 mt-1">{method.description}</p>
                    <p className="text-sm text-gray-400 mt-2">
                      {((method.value / totalFiles) * 100 || 0).toFixed(0)}% of files
                    </p>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <ScanLine className="w-12 h-12 text-gray-300 mb-3" />
              <p className="font-medium">No extraction data available</p>
              <p className="text-sm">Process some files to see methods used</p>
            </div>
          )}

          {/* Explanation */}
          <div className="mt-6 p-4 rounded-xl bg-gray-100 border border-gray-200">
            <p className="text-sm text-gray-600">
              <strong>💡 Tip:</strong> "Clean PDFs" (digital documents) provide the most accurate data extraction.
              "Scanned Images" require OCR technology and may have slightly lower accuracy.
              If you have many scanned documents, consider using digital PDFs when possible for better search results.
            </p>
          </div>
        </div>
      </div>

      {/* Processing Activity */}
      {procStats && (
        <div className="card">
          <div className="card-header">
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-gradient-to-br from-success-100 to-success-50 mr-3">
                <TrendingUp className="w-5 h-5 text-success-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Processing Activity</h3>
                <p className="text-xs text-gray-500">System performance and recent activity</p>
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-xl">
                <Clock className="w-8 h-8 mx-auto text-primary-600 mb-2" />
                <p className="text-2xl font-bold text-gray-900">
                  {procStats.avg_processing_time_ms?.toFixed(0) || 0}
                </p>
                <p className="text-sm font-medium text-gray-700">milliseconds</p>
                <p className="text-xs text-gray-500 mt-1">Average per file</p>
              </div>

              <div className="text-center p-4 bg-gray-50 rounded-xl">
                <TrendingUp className="w-8 h-8 mx-auto text-success-600 mb-2" />
                <p className="text-2xl font-bold text-gray-900">
                  {procStats.high_confidence_rate?.toFixed(0) || 0}%
                </p>
                <p className="text-sm font-medium text-gray-700">Reliability rate</p>
                <p className="text-xs text-gray-500 mt-1">High confidence files</p>
              </div>

              <div className="text-center p-4 bg-gray-50 rounded-xl">
                <ImageIcon className="w-8 h-8 mx-auto text-warning-600 mb-2" />
                <p className="text-2xl font-bold text-gray-900">
                  {procStats.ocr_used_count || 0}
                </p>
                <p className="text-sm font-medium text-gray-700">Scanned images</p>
                <p className="text-xs text-gray-500 mt-1">Read using OCR</p>
              </div>

              <div className="text-center p-4 bg-gray-50 rounded-xl">
                <Zap className="w-8 h-8 mx-auto text-purple-600 mb-2" />
                <p className="text-2xl font-bold text-gray-900">
                  {procStats.total_processing_time_hours?.toFixed(1) || 0}
                </p>
                <p className="text-sm font-medium text-gray-700">Total hours</p>
                <p className="text-xs text-gray-500 mt-1">Processing time</p>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="font-semibold text-gray-800 mb-4 flex items-center">
                <Clock className="w-4 h-4 mr-2 text-gray-500" />
                Recent Activity
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-4 bg-success-50 rounded-xl border border-success-100">
                  <div>
                    <p className="text-sm text-success-700">Files processed today</p>
                    <p className="text-xs text-success-600 mt-0.5">New additions to your library</p>
                  </div>
                  <span className="text-3xl font-bold text-success-700">
                    {procStats.files_processed_today || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between p-4 bg-primary-50 rounded-xl border border-primary-100">
                  <div>
                    <p className="text-sm text-primary-700">Files this week</p>
                    <p className="text-xs text-primary-600 mt-0.5">Weekly processing total</p>
                  </div>
                  <span className="text-3xl font-bold text-primary-700">
                    {procStats.files_processed_this_week || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        <button className="btn btn-secondary flex items-center">
          <Download className="w-4 h-4 mr-2" />
          Download Report
        </button>
        <button
          className="btn btn-secondary flex items-center"
          onClick={() => setShowReviewModal(true)}
          disabled={lowConfidenceCount === 0}
        >
          <Eye className="w-4 h-4 mr-2" />
          View Files Needing Review ({lowConfidenceCount})
        </button>
        <button className="btn btn-secondary flex items-center">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh Statistics
        </button>
      </div>

      {/* Review Files Modal */}
      {showReviewModal && (
        <ReviewFilesModal onClose={() => setShowReviewModal(false)} />
      )}
    </div>
  )
}

// Modal component for showing files that need review
function ReviewFilesModal({ onClose }) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['needs-review-files'],
    queryFn: async () => {
      const response = await specificationsApi.list({
        needs_review: true,
        limit: 100,
        sort_by: 'created_at',
        sort_order: 'desc'
      })
      return response.data
    }
  })

  const files = data?.items || []

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[80vh] mx-4 overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-warning-50 to-orange-50">
          <div className="flex items-center">
            <div className="p-2 bg-warning-100 rounded-lg mr-3">
              <AlertTriangle className="w-6 h-6 text-warning-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Files That Need Your Attention</h2>
              <p className="text-sm text-gray-500">
                {files.length} file{files.length !== 1 ? 's' : ''} may have uncertain data
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => refetch()}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh list"
            >
              <RefreshCw className={`w-5 h-5 text-gray-500 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Explanation */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-100">
          <p className="text-sm text-gray-600">
            <strong>Why are these files listed?</strong> The system wasn't confident about the data it extracted from these files.
            This could be because the PDF was scanned, had poor image quality, or used an unusual format.
          </p>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(80vh-200px)]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-primary-500 animate-spin" />
              <span className="ml-3 text-gray-500">Loading files...</span>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12 text-red-500">
              <AlertTriangle className="w-6 h-6 mr-2" />
              Failed to load files. Please try again.
            </div>
          ) : files.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <CheckCircle className="w-12 h-12 text-success-500 mb-3" />
              <p className="font-medium text-gray-800">Great news! No files need review</p>
              <p className="text-sm">All your drawings have reliable data.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {files.map((file, index) => (
                <div
                  key={file.id}
                  className="px-6 py-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start flex-1 min-w-0">
                      <div className="p-2 bg-gray-100 rounded-lg mr-3 flex-shrink-0">
                        <FileText className="w-5 h-5 text-gray-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-gray-900">
                            {file.section_number || `File #${index + 1}`}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            (file.extraction_confidence || 0) < 0.5
                              ? 'bg-danger-100 text-danger-700'
                              : 'bg-warning-100 text-warning-700'
                          }`}>
                            {((file.extraction_confidence || 0) * 100).toFixed(0)}% reliable
                          </span>
                        </div>
                        {/* File Path */}
                        <div className="mt-1 flex items-center text-sm text-gray-500">
                          <FolderOpen className="w-4 h-4 mr-1 flex-shrink-0" />
                          <span className="truncate" title={file.source_path || file.file_path || 'Path not available'}>
                            {file.source_path || file.file_path || 'Path not available'}
                          </span>
                        </div>
                        {/* Additional Info */}
                        <div className="mt-2 flex items-center gap-4 text-xs text-gray-400">
                          {file.profile_type && (
                            <span>Type: {PROFILE_TYPE_NAMES[file.profile_type] || file.profile_type}</span>
                          )}
                          {file.alloy && (
                            <span>Alloy: {file.alloy}</span>
                          )}
                          {file.created_at && (
                            <span>Added: {new Date(file.created_at).toLocaleDateString()}</span>
                          )}
                        </div>
                        {/* Review Reason */}
                        {file.notes && (
                          <div className="mt-2 text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded">
                            Note: {file.notes}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50">
          <p className="text-xs text-gray-500 text-center">
            💡 Files are flagged when extraction confidence is below 60%. Review them to ensure accurate search results.
          </p>
        </div>
      </div>
    </div>
  )
}
